import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, FileText } from 'lucide-react';
import Footer from '@/components/Footer';

export default function TermsOfServicePage() {
  const lastUpdated = "February 24, 2026";

  return (
    <div className="min-h-screen bg-cream font-body">
      {/* Header */}
      <header className="bg-navy text-white py-6 px-6 md:px-12">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="font-display text-xl">
            Cali<span className="italic-teal">Moms</span> Connect
          </Link>
          <Link 
            to="/" 
            className="flex items-center gap-2 text-white/70 hover:text-white text-sm"
          >
            <ArrowLeft size={16} /> Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 md:px-12 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 bg-teal/10 rounded-full flex items-center justify-center">
              <FileText className="text-teal" size={24} />
            </div>
            <div>
              <h1 className="font-display text-4xl text-navy">Terms of Service</h1>
              <p className="text-text-mid text-sm mt-1">Last updated: {lastUpdated}</p>
            </div>
          </div>

          <div className="prose prose-lg max-w-none">
            <div className="bg-white rounded-2xl p-8 shadow-card space-y-8">
              
              {/* Acceptance of Terms */}
              <section>
                <h2 className="font-display text-2xl text-navy mb-4">1. Acceptance of Terms</h2>
                <p className="text-text-mid leading-relaxed">
                  Welcome to Cali Moms Connect. By accessing or using our platform, website, or services (collectively, the "Services"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our Services.
                </p>
                <p className="text-text-mid leading-relaxed mt-4">
                  These Terms constitute a legally binding agreement between you and Cali Moms Connect ("we," "our," or "us"). We reserve the right to modify these Terms at any time. We will notify you of material changes by posting the updated Terms on our platform. Your continued use of the Services after such changes constitutes your acceptance of the new Terms.
                </p>
              </section>

              {/* Eligibility */}
              <section>
                <h2 className="font-display text-2xl text-navy mb-4">2. Eligibility</h2>
                <p className="text-text-mid leading-relaxed">
                  To use Cali Moms Connect, you must:
                </p>
                <ul className="list-disc list-inside text-text-mid space-y-2 mt-3 ml-4">
                  <li>Be at least 18 years of age</li>
                  <li>Be a legal resident of California or have significant ties to California</li>
                  <li>Be a mother or expecting mother</li>
                  <li>Have the legal capacity to enter into a binding agreement</li>
                  <li>Not have been previously banned from our Services</li>
                </ul>
                <p className="text-text-mid leading-relaxed mt-4">
                  By creating an account, you represent and warrant that you meet all eligibility requirements.
                </p>
              </section>

              {/* Membership Tiers and Billing */}
              <section>
                <h2 className="font-display text-2xl text-navy mb-4">3. Membership Tiers and Billing</h2>
                
                <h3 className="font-semibold text-navy text-lg mt-6 mb-3">Membership Options</h3>
                <p className="text-text-mid leading-relaxed">We offer two membership tiers:</p>
                <ul className="list-disc list-inside text-text-mid space-y-2 mt-3 ml-4">
                  <li><strong>Connect (Free):</strong> Basic community access, limited AI matching, limited messaging, access to free events, and community marketplace</li>
                  <li><strong>Thrive (Paid — $39/month or $390/year):</strong> Everything in Connect, plus priority and enhanced AI matching, unlimited messaging, curated Circles, members-only events, local business discounts, one workshop included every month, 30% off additional workshops, and 24-hour early workshop registration</li>
                </ul>

                <h3 className="font-semibold text-navy text-lg mt-6 mb-3">Founding Member Status</h3>
                <p className="text-text-mid leading-relaxed">
                  Members who subscribed to Thrive at the earlier $24.99/month rate keep that price permanently for as long as they remain continuously subscribed, and receive all Thrive benefits including the monthly workshop credit. Members who subscribe to Thrive before September 30, 2026 earn permanent Founding Member status, which includes 48-hour early workshop registration access. Annual subscribers additionally receive one guest pass per subscription year, allowing one guest to attend a single workshop free; guest passes expire at renewal, are non-transferable, and cannot be converted to credit.
                </p>

                <h3 className="font-semibold text-navy text-lg mt-6 mb-3">Billing</h3>
                <p className="text-text-mid leading-relaxed">
                  Thrive is billed monthly until cancelled. By subscribing, you authorize us to charge your payment method on file for the subscription fee at the beginning of each billing cycle.
                </p>
                <p className="text-text-mid leading-relaxed mt-4">
                  All payments are processed securely through Stripe. We do not store your full credit card information on our servers.
                </p>

                <h3 className="font-semibold text-navy text-lg mt-6 mb-3">Price Changes</h3>
                <p className="text-text-mid leading-relaxed">
                  We reserve the right to change our pricing at any time. If we change the price of your subscription, we will notify you at least 30 days before the price change takes effect. Your continued use after the price change constitutes your acceptance of the new price.
                </p>
              </section>

              {/* Cancellation and Refunds */}
              <section>
                <h2 className="font-display text-2xl text-navy mb-4">4. Cancellation and Refund Policy</h2>
                
                <h3 className="font-semibold text-navy text-lg mt-6 mb-3">Cancellation</h3>
                <p className="text-text-mid leading-relaxed">
                  You may cancel your paid membership at any time through your account settings or by contacting us at <a href="mailto:hello@calimomsconnect.com" className="text-teal hover:text-teal-deep underline">hello@calimomsconnect.com</a>. Upon cancellation:
                </p>
                <ul className="list-disc list-inside text-text-mid space-y-2 mt-3 ml-4">
                  <li>Your membership will remain active until the end of your current billing period</li>
                  <li>You will not be charged for subsequent billing periods</li>
                  <li>After your membership expires, your account will revert to the free Connect tier</li>
                </ul>

                <h3 className="font-semibold text-navy text-lg mt-6 mb-3">Refunds</h3>
                <p className="text-text-mid leading-relaxed">
                  Due to the nature of our digital services, we generally do not offer refunds for partial billing periods. However, we may consider refund requests on a case-by-case basis in the following circumstances:
                </p>
                <ul className="list-disc list-inside text-text-mid space-y-2 mt-3 ml-4">
                  <li>Technical issues that prevented you from accessing the Services for an extended period</li>
                  <li>Duplicate charges or billing errors</li>
                  <li>Cancellation within 48 hours of initial subscription (first-time subscribers only)</li>
                </ul>
                <p className="text-text-mid leading-relaxed mt-4">
                  To request a refund, please contact us at <a href="mailto:hello@calimomsconnect.com" className="text-teal hover:text-teal-deep underline">hello@calimomsconnect.com</a> with details about your request.
                </p>
              </section>

              {/* Member Conduct */}
              <section>
                <h2 className="font-display text-2xl text-navy mb-4">5. Member Conduct and Community Guidelines</h2>
                <p className="text-text-mid leading-relaxed">
                  Cali Moms Connect is built on mutual respect and authentic connections. By using our Services, you agree to:
                </p>
                
                <h3 className="font-semibold text-navy text-lg mt-6 mb-3">You Will:</h3>
                <ul className="list-disc list-inside text-text-mid space-y-2 ml-4">
                  <li>Provide accurate and truthful information in your profile</li>
                  <li>Treat other members with respect and kindness</li>
                  <li>Respect the privacy of other members and keep shared information confidential</li>
                  <li>Use the platform for its intended purpose of building genuine connections</li>
                  <li>Report any suspicious or inappropriate behavior to our team</li>
                </ul>

                <h3 className="font-semibold text-navy text-lg mt-6 mb-3">You Will Not:</h3>
                <ul className="list-disc list-inside text-text-mid space-y-2 ml-4">
                  <li>Harass, bully, threaten, or intimidate other members</li>
                  <li>Post or share content that is offensive, discriminatory, sexually explicit, or illegal</li>
                  <li>Use the platform for commercial solicitation, spam, or advertising without permission</li>
                  <li>Impersonate another person or misrepresent your identity</li>
                  <li>Attempt to collect personal information from other members for unauthorized purposes</li>
                  <li>Use automated systems or bots to access the Services</li>
                  <li>Share your account credentials or allow others to access your account</li>
                  <li>Circumvent any security measures or access restrictions</li>
                  <li>Engage in any activity that could harm the reputation or operation of Cali Moms Connect</li>
                </ul>
              </section>

              {/* Content Ownership */}
              <section>
                <h2 className="font-display text-2xl text-navy mb-4">6. Content Ownership and License</h2>
                
                <h3 className="font-semibold text-navy text-lg mt-6 mb-3">Your Content</h3>
                <p className="text-text-mid leading-relaxed">
                  You retain ownership of all content you submit to the platform, including profile information, photos, messages, and posts ("Your Content"). By submitting Your Content, you grant Cali Moms Connect a non-exclusive, worldwide, royalty-free license to use, reproduce, modify, and display Your Content solely for the purpose of operating and improving the Services.
                </p>
                <p className="text-text-mid leading-relaxed mt-4">
                  You represent and warrant that you have the right to submit Your Content and that it does not infringe on any third party's rights.
                </p>

                <h3 className="font-semibold text-navy text-lg mt-6 mb-3">Our Content</h3>
                <p className="text-text-mid leading-relaxed">
                  The Services, including all software, design, text, graphics, logos, and other materials, are owned by Cali Moms Connect and are protected by copyright, trademark, and other intellectual property laws. You may not copy, modify, distribute, or create derivative works from our content without our express written permission.
                </p>
              </section>

              {/* Matching Technology Disclaimer */}
              <section>
                <h2 className="font-display text-2xl text-navy mb-4">7. Matching Technology Disclaimer</h2>
                <p className="text-text-mid leading-relaxed">
                  Cali Moms Connect uses proprietary compatibility matching technology to suggest potential connections based on member-provided profile information. However, CMC does not guarantee that any member will receive matches, that matches will result in friendships or ongoing relationships, or that suggested matches will meet any particular standard of compatibility. Match results are based solely on information members choose to provide and may vary. CMC expressly disclaims any liability arising from unmet expectations related to the matching feature.
                </p>
              </section>

              {/* Member Interactions and Safety */}
              <section>
                <h2 className="font-display text-2xl text-navy mb-4">8. Member Interactions and Safety</h2>
                <p className="text-text-mid leading-relaxed">
                  Cali Moms Connect is a platform that facilitates introductions between members. Any decision to meet, communicate, or interact with another member — whether online or in person — is made solely at your own risk. CMC does not conduct criminal background checks on members unless explicitly stated, does not verify the accuracy of member-provided information, and is not responsible for the conduct, statements, or actions of any member before, during, or after interactions facilitated through the platform. Members are strongly encouraged to exercise personal judgment and take reasonable safety precautions when meeting other members in person, including meeting in public places and informing a trusted person of their plans. CMC expressly disclaims any liability for damages, injury, or loss arising from member interactions.
                </p>
              </section>

              {/* Limitation of Liability */}
              <section>
                <h2 className="font-display text-2xl text-navy mb-4">9. Limitation of Liability</h2>
                <p className="text-text-mid leading-relaxed">
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW:
                </p>
                <ul className="list-disc list-inside text-text-mid space-y-2 mt-3 ml-4">
                  <li>THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED</li>
                  <li>WE DO NOT GUARANTEE THAT YOU WILL MAKE ANY CONNECTIONS, FRIENDSHIPS, OR RELATIONSHIPS THROUGH OUR SERVICES</li>
                  <li>WE ARE NOT RESPONSIBLE FOR THE CONDUCT OF ANY MEMBER, WHETHER ONLINE OR OFFLINE</li>
                  <li>WE ARE NOT LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE SERVICES</li>
                  <li>OUR TOTAL LIABILITY TO YOU SHALL NOT EXCEED THE AMOUNT YOU PAID TO US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM</li>
                </ul>
                <p className="text-text-mid leading-relaxed mt-4">
                  You acknowledge that you use the Services at your own risk and are solely responsible for your interactions with other members.
                </p>
              </section>

              {/* Indemnification */}
              <section>
                <h2 className="font-display text-2xl text-navy mb-4">10. Indemnification and Release of Liability</h2>
                <p className="text-text-mid leading-relaxed">
                  You agree to indemnify, defend, and hold harmless Cali Moms Connect, its officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, and expenses (including reasonable attorneys' fees) arising out of or in any way connected with:
                </p>
                <ul className="list-disc list-inside text-text-mid space-y-2 mt-3 ml-4">
                  <li>Your use of the Services</li>
                  <li>Your violation of these Terms</li>
                  <li>Your violation of any third party's rights</li>
                  <li>Your Content</li>
                </ul>

                <h3 className="font-semibold text-navy text-lg mt-6 mb-3">Release of Liability</h3>
                <p className="text-text-mid leading-relaxed">
                  By creating an account and using Cali Moms Connect, you agree to release, indemnify, and hold harmless Cali Moms Connect, its founder, officers, employees, and agents from any and all claims, damages, losses, or expenses — including reasonable attorney's fees — arising out of or related to your use of the platform, your interactions with other members, or your attendance at any event listed on or organized through the platform.
                </p>
              </section>

              {/* Account Termination */}
              <section>
                <h2 className="font-display text-2xl text-navy mb-4">11. Account Termination</h2>
                <p className="text-text-mid leading-relaxed">
                  We reserve the right to suspend or terminate your account at any time, with or without notice, for any reason, including but not limited to:
                </p>
                <ul className="list-disc list-inside text-text-mid space-y-2 mt-3 ml-4">
                  <li>Violation of these Terms or our Community Guidelines</li>
                  <li>Fraudulent, abusive, or illegal activity</li>
                  <li>Behavior that harms other members or our platform</li>
                  <li>Providing false or misleading information</li>
                  <li>Non-payment of fees</li>
                  <li>Extended period of inactivity</li>
                </ul>
                <p className="text-text-mid leading-relaxed mt-4">
                  If your account is terminated for violation of these Terms, you will not be entitled to any refund. You may appeal a termination decision by contacting us at <a href="mailto:hello@calimomsconnect.com" className="text-teal hover:text-teal-deep underline">hello@calimomsconnect.com</a>.
                </p>
              </section>

              {/* Dispute Resolution */}
              <section>
                <h2 className="font-display text-2xl text-navy mb-4">12. Dispute Resolution</h2>
                <p className="text-text-mid leading-relaxed">
                  Any dispute arising from or relating to these Terms or the Services shall first be attempted to be resolved through informal negotiation. If we cannot resolve the dispute informally within 30 days, either party may pursue binding arbitration in accordance with the rules of the American Arbitration Association.
                </p>
                <p className="text-text-mid leading-relaxed mt-4">
                  You agree that any dispute resolution proceedings will be conducted only on an individual basis and not as a class, consolidated, or representative action.
                </p>
              </section>

              {/* Governing Law */}
              <section>
                <h2 className="font-display text-2xl text-navy mb-4">13. Governing Law</h2>
                <p className="text-text-mid leading-relaxed">
                  These Terms shall be governed by and construed in accordance with the laws of the State of California, without regard to its conflict of law provisions. Any legal action or proceeding arising under these Terms shall be brought exclusively in the state or federal courts located in California, and you hereby consent to the personal jurisdiction of such courts.
                </p>
              </section>

              {/* Severability */}
              <section>
                <h2 className="font-display text-2xl text-navy mb-4">14. Severability</h2>
                <p className="text-text-mid leading-relaxed">
                  If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary so that these Terms shall otherwise remain in full force and effect.
                </p>
              </section>

              {/* Entire Agreement */}
              <section>
                <h2 className="font-display text-2xl text-navy mb-4">15. Entire Agreement</h2>
                <p className="text-text-mid leading-relaxed">
                  These Terms, together with our Privacy Policy, constitute the entire agreement between you and Cali Moms Connect regarding your use of the Services and supersede all prior agreements and understandings.
                </p>
              </section>

              {/* Contact */}
              <section>
                <h2 className="font-display text-2xl text-navy mb-4">16. Contact Information</h2>
                <p className="text-text-mid leading-relaxed">
                  If you have any questions about these Terms, please contact us at:
                </p>
                <div className="bg-cream rounded-xl p-6 mt-4">
                  <p className="text-navy font-semibold">Cali Moms Connect</p>
                  <p className="text-text-mid mt-2">Email: <a href="mailto:hello@calimomsconnect.com" className="text-teal hover:text-teal-deep underline">hello@calimomsconnect.com</a></p>
                  <p className="text-text-mid mt-1">Location: California, United States</p>
                </div>
              </section>

            </div>
          </div>
        </motion.div>
      </main>

      <Footer />
    </div>
  );
}
